import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveSupportedLanguage } from '@/constants/languages';
import { changeLanguage } from '@/i18n';

const applyLanguage = (
  raw: string,
  currentLanguage: string,
  changeLanguage: (lng: string) => void
) => {
  // Resolved, not membership-tested: a tab on an older build can leave a
  // region-tagged or lowercased code in `vm_language` (`zh-tw`). Resolving keeps
  // cross-tab sync on the same spelling as the picker (FR-3).
  const code = resolveSupportedLanguage(raw);
  if (code && code !== currentLanguage) {
    changeLanguage(code);
  }
};

/**
 * Keeps the active i18n language in sync across tabs and devices.
 *
 * - Cross-tab: the `storage` event fires when another tab calls
 *   i18n.changeLanguage(), which writes `vm_language` to localStorage.
 *
 * Must be mounted inside AuthProvider.
 */
export function useLanguageSync() {
  const { i18n } = useTranslation();

  // Cross-tab sync via the localStorage storage event.
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key !== 'vm_language' || !e.newValue) return;
      applyLanguage(e.newValue, i18n.language, changeLanguage);
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [i18n]);
}
